import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";

type State = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState<State>("loading");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: ANON } }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setState("invalid");
          return;
        }
        if (data.alreadyUnsubscribed || data.already_unsubscribed) {
          setState("already");
        } else if (data.valid !== false) {
          setState("valid");
        } else {
          setState("invalid");
        }
      } catch {
        setState("invalid");
      }
    })();
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    const { error } = await supabase.functions.invoke("handle-email-unsubscribe", {
      body: { token },
    });
    setSubmitting(false);
    setState(error ? "error" : "success");
  };

  return (
    <>
      <Helmet>
        <title>Avregistrera e-post – Currylicious Yum Yum</title>
        <meta name="description" content="Avregistrera dig från e-postmeddelanden från Currylicious Yum Yum." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.currylicious.se/unsubscribe" />
        <meta property="og:title" content="Avregistrera e-post – Currylicious Yum Yum" />
        <meta property="og:description" content="Hantera din e-postprenumeration från Currylicious Yum Yum." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.currylicious.se/unsubscribe" />
      </Helmet>
      <main className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full text-center border border-primary/20 rounded-2xl p-10 bg-card">
          <h1 className="text-2xl font-serif text-primary mb-4">Avregistrera e-post</h1>
          {state === "loading" && <p className="text-muted-foreground">Kontrollerar länken…</p>}
          {state === "invalid" && <p className="text-muted-foreground">Den här avregistreringslänken är ogiltig eller har gått ut.</p>}
          {state === "already" && <p className="text-muted-foreground">Denna e-postadress är redan avregistrerad.</p>}
          {state === "valid" && (
            <>
              <p className="text-muted-foreground mb-6">Klicka nedan för att avregistrera dig från mejl från Currylicious.</p>
              <button
                onClick={confirm}
                disabled={submitting}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
              >
                {submitting ? "Avregistrerar…" : "Bekräfta avregistrering"}
              </button>
            </>
          )}
          {state === "success" && <p className="text-muted-foreground">Du är nu avregistrerad. Du kommer inte längre att få mejl från oss.</p>}
          {state === "error" && <p className="text-destructive">Något gick fel. Försök igen senare.</p>}
        </div>
      </main>
    </>
  );
};

export default Unsubscribe;